import { useId, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  type BuyerIntroEmailStateDto,
} from "@/api/buyers";
import type { BuyerSendVersionDto } from "@/api/buyerSendVersions";
import {
  Button,
  EmptyState,
  Skeleton,
  Textarea,
  Timestamp,
  useToast,
} from "@/design-system/components/ui";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { cn } from "@/design-system/lib/utils";
import { useSendHistory } from "@/hooks/buyers/useSendHistory";
import { withoutListingPages } from "@/lib/listingPageUrl";
import { useDateFormatter } from "@/utils/datetime";
import { CollapsibleCard } from "../introCards/CollapsibleCard";
import { HeaderAction } from "../introCards/HeaderAction";
import { InfoHint } from "../introCards/InfoHint";
import { introCardCopy } from "../introCards/introCardCopy";
import { IntroCardPill } from "../introCards/IntroCardPill";
import { draftNewerThanSend, isDone, latestVersion, messageCardStatus } from "../introCards/introCardStatus";
import { EditAndSendAgain, SentFold } from "../introCards/SentFold";
import { InlineQueryError } from "../queryStates";
import { combinedEntry } from "./combinedEntry";
import { IntroEmailCopyModal } from "./IntroEmailCopyModal";
import { IntroEmailSendModal } from "./IntroEmailSendModal";
import { useIntroEmailState } from "./useIntroEmailState";

/**
 * The buyer-facing listing intro email — step 4 of the Buyer Consultant's loop.
 *
 * <p>🔴 <b>A SIBLING of the package panel, and it must never be mistaken for
 * it.</b> The package goes to the LISTING BROKER once a buyer has said yes;
 * this goes to the BUYER, to find out whether there is anything to take to a
 * broker at all. They sit on the same page, so every label here names the
 * recipient rather than the artifact — "to the buyer", "{buyer} at {address}",
 * "before any submission package goes to the listing broker". A specialist must
 * never be one mis-click from writing to the wrong audience.</p>
 *
 * <p><b>Draft, read, correct, send.</b> Generation is its own action and the
 * server refuses a send that never generated, so the assistant's words cannot
 * reach a buyer without a specialist having had them on screen. This surface
 * makes that step real rather than nominal: the whole body renders expanded and
 * editable, the send controls sit BELOW it, and an unsaved edit disables them —
 * so the thing sent is the thing that was read.</p>
 *
 * <p>🔴 <b>There is deliberately no "I have edited this" gate.</b> The server's
 * enforced condition is "a draft exists that a human has had in front of them",
 * not "a human changed a character" — a draft that came out right should not
 * have to be perturbed to be sendable, and nothing can distinguish a considered
 * acceptance from an ignored one. Inventing a client-side gate would contradict
 * that and teach specialists to type a space.</p>
 *
 * <p>🔴 <b>Both send paths stamp.</b> Taking the copy for your own mail client
 * records the contact exactly as a Phoenix send does, so a specialist who
 * prefers their own mailbox is not invisible in the funnel. The UI says so
 * before either button is pressed.</p>
 *
 * <p><b>The Buyer intro tab's collapsible card (D2).</b> The tab owns whether it
 * is open. Its one pill is the LATEST send version's (J2) — "combined" when it
 * went out inside a combined intro. Once sent, what went out is folded (a
 * combined send shows this listing's own entry when it can be found by its
 * number, J4), the composer waits behind "Edit and send again" unless the draft
 * is newer than the send (J8), and the "nothing drafted" empty state gives way
 * to a small "Draft a separate intro email for this listing" link (D10). The
 * typed draft lives here, above the hidden body, so a collapse keeps it.</p>
 */
export function IntroEmailPanel({
  submissionId,
  canWrite,
  open,
  onOpenChange,
  onActed,
}: {
  submissionId: string;
  /**
   * 🔴 The page's one write answer — `s.canEdit && hasPermission("Submissions.Manage")`.
   *
   * <p>REQUIRED, and deliberately not defaulted. The sibling package panel
   * shipped with no permission prop at all, so Generate / Send rendered for
   * every read-only role and 403'd on click. A required prop cannot be
   * forgotten; a default of `true` would reproduce the defect, and a default of
   * `false` would let a caller silently lose the affordances instead.</p>
   *
   * <p>The READ stays ungated — a colleague who cannot send still has every
   * reason to see whether this buyer has been contacted, and what they were
   * told.</p>
   */
  canWrite: boolean;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Called after a send (or a recorded copy) this tab performed (J6). */
  onActed?: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [sending, setSending] = useState(false);
  const [copying, setCopying] = useState(false);
  const [revealed, setRevealed] = useState(false);
  // R3 — a SEND-TIME choice, off by default and never saved: the single intro
  // has no draft row to hold it, and the next send starts from off again. Held
  // here rather than in the composer so the send and copy modals read the same
  // value the checkbox shows.
  const [includeListingLink, setIncludeListingLink] = useState(false);

  const state = useIntroEmailState(submissionId);
  const history = useSendHistory(submissionId);

  // The typed draft, reseeded when a (re)draft lands — what keying the composer
  // on `generatedAtUtc` did when the text lived inside it.
  const savedBody = state.data?.bodyDraft ?? "";
  const seed = state.data?.generatedAtUtc ?? null;
  const [seededFrom, setSeededFrom] = useState(seed);
  const [draft, setDraft] = useState(savedBody);
  if (seed !== seededFrom) {
    setSeededFrom(seed);
    setDraft(savedBody);
  }

  const generate = useMutation({
    mutationFn: () => buyerSubmissionsApi.generateIntroEmail(submissionId),
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success(
        s.aiModel
          ? "Draft ready. Read it through before you send it."
          : "Draft ready — composed from the listing's facts. Read it through before you send it.",
      );
    },
    // The 422 the daily AI ceiling raises arrives here with the server's own
    // sentence, which names the ceiling. Never compose a second one.
    onError: (e: Error) => toast.error(e.message),
  });

  const latest = latestVersion(history.data?.introEmails);
  const status = messageCardStatus({
    generatedAtUtc: state.data?.generatedAtUtc ?? null,
    sentAtUtc: state.data?.sentAtUtc ?? null,
    latest,
  });

  const card = (body: ReactNode, extras?: { info?: ReactNode; action?: ReactNode; pill?: boolean }) => (
    <CollapsibleCard
      open={open}
      onOpenChange={onOpenChange}
      title="Listing intro email"
      info={extras?.info}
      recipient="To the buyer"
      pill={extras?.pill ? <IntroCardPill status={status} /> : undefined}
      action={extras?.action}
      testId="buyer-intro-card"
    >
      {body}
    </CollapsibleCard>
  );

  if (state.isLoading) return card(<Skeleton className="h-40 w-full" />);
  // Never `return null`. A vanished section is the least recoverable dishonest
  // state: nothing is left on screen to retry from, and nothing says an intro
  // email section should be there at all.
  if (state.isError) {
    return card(
      <InlineQueryError
        what="the intro email"
        onRetry={() => void state.refetch()}
        retrying={state.isFetching}
      />,
    );
  }
  if (!state.data) return null;

  const email = state.data;
  const drafted = Boolean(email.generatedAtUtc);
  const done = isDone(status);
  const sentAt = status.state === "sent" ? status.date : email.sentAtUtc;
  // A tick with no qualifying page adds nothing, so it is never sent as true —
  // the server would refuse nothing, but the preview would show a line the
  // email cannot carry.
  const withListingLink = includeListingLink && email.listingPageLine != null;
  const empty = draft.trim().length === 0;
  // Trimmed both sides: the server trims on save, so an untrimmed comparison
  // would leave the panel permanently dirty after a save.
  const dirty = draft.trim() !== (email.bodyDraft ?? "").trim();
  const drifted = sentDrifted(email);
  // J8 — unsent work: a draft generated after the latest send, or a saved
  // draft that no longer says what went out (the DTO carries no save time; the
  // drift the snapshot already reports is that fact).
  const unsentDraft = drafted && (draftNewerThanSend([email.generatedAtUtc], sentAt) || drifted);
  // J8 — writers only: a reader keeps today's read-only view, with no control to reach.
  // The editor area hides by CLASS as well as the attribute: Tailwind's preflight
  // `[hidden]` rule loses to `.grid` (same specificity, emitted earlier).
  const editorHidden = canWrite && done && !revealed && !unsentDraft;
  const acted = () => {
    setRevealed(false);
    onActed?.();
  };

  const action = !canWrite ? null : status.state === "notDrafted" ? (
    <HeaderAction
      label={generate.isPending ? "Drafting…" : "Draft intro email"}
      onClick={() => generate.mutate()}
      disabled={generate.isPending}
      testId="intro-email-header-draft"
    />
  ) : status.state === "draftSaved" ? (
    <HeaderAction
      label="Send from Phoenix"
      onClick={() => setSending(true)}
      disabled={dirty || empty || !email.canSend}
      reason={dirty ? UNSAVED : email.canSend ? null : email.blockedReason}
      testId="intro-email-header-send"
    />
  ) : null;

  return card(
    <section className="grid gap-space-4" data-testid="intro-email-panel">
      {done && latest ? <IntroSentLine version={latest} /> : null}

      {done ? <SentSnapshot email={email} drifted={drifted} /> : null}

      {!drafted && done ? (
        // D10 — a send exists, so the "nothing drafted" empty state would
        // contradict the email above it. Today's draft flow, behind a link.
        canWrite ? (
          <div>
            <Button
              variant="text"
              size="sm"
              onClick={() => generate.mutate()}
              disabled={generate.isPending}
              data-testid="intro-email-draft-separate"
            >
              {generate.isPending ? "Drafting…" : introCardCopy.draftSeparateIntro}
            </Button>
          </div>
        ) : (
          <p className="text-small text-gray-mid m-0">
            No intro email has been drafted yet. Only this submission&apos;s
            consultant can draft one.
          </p>
        )
      ) : (
        <>
          {editorHidden && canWrite ? (
            <EditAndSendAgain onClick={() => setRevealed(true)} testId="intro-email-edit-again" />
          ) : null}

          <div hidden={editorHidden} className={cn("grid gap-space-4", editorHidden && "hidden")} data-testid="intro-email-editor">
            {!drafted ? (
              <NotDrafted
                canWrite={canWrite}
                onDraft={() => generate.mutate()}
                drafting={generate.isPending}
              />
            ) : canWrite ? (
              <IntroEmailComposer
                submissionId={submissionId}
                email={email}
                draft={draft}
                onDraftChange={setDraft}
                dirty={dirty}
                empty={empty}
                onSend={() => setSending(true)}
                onCopy={() => setCopying(true)}
                onRedraft={() => generate.mutate()}
                redrafting={generate.isPending}
                includeListingLink={withListingLink}
                onIncludeListingLinkChange={setIncludeListingLink}
              />
            ) : (
              <ReadOnlyDraft email={email} />
            )}
          </div>
        </>
      )}

      {sending ? (
        <IntroEmailSendModal
          submissionId={submissionId}
          email={email}
          includeListingLink={withListingLink}
          onSent={acted}
          onClose={() => setSending(false)}
        />
      ) : null}

      {copying ? (
        <IntroEmailCopyModal
          submissionId={submissionId}
          email={email}
          includeListingLink={withListingLink}
          onRecorded={acted}
          onClose={() => setCopying(false)}
        />
      ) : null}
    </section>,
    { info: <InfoHint text={explainerText(email)} testId="intro-email-info" />, action, pill: true },
  );
}

/** The sentence an unsaved edit shows — under the composer, and as the collapsed
 *  header's SEND tooltip (J5). */
const UNSAVED = "Save your changes before sending — the send uses the saved draft, not what is on screen.";

/**
 * D6 — the audience, the moment, and Phoenix's role, in one sentence, behind
 * the ⓘ beside the card's title, word for word. Phoenix is the loan and note
 * administrator, never a broker, and this email is preliminary: the same two
 * constraints the prompt and the deterministic composer are both held to.
 */
function explainerText(email: BuyerIntroEmailStateDto): string {
  return (
    `An introduction to ${email.listingName} for ${email.buyerName} — sent to the ` +
    "buyer, before any submission package goes to the listing broker. Phoenix " +
    "Capital is the loan and note administrator, so it is preliminary: nothing " +
    "in it is an offer, an approval, or a recommendation."
  );
}

/**
 * Whether the saved draft no longer says what went out. R7 — a combined send's
 * body is the WHOLE email, introducing this listing among others, so it can
 * never equal this listing's own draft; comparing them would report an edit
 * nobody made. The section the system added after the message is not part of
 * what the specialist wrote, so it is taken off before asking.
 */
function sentDrifted(email: BuyerIntroEmailStateDto): boolean {
  const combined = email.latestSendCombinedIntroEmailId != null;
  return (
    !combined &&
    email.sentBody != null &&
    email.bodyDraft != null &&
    withoutListingPages(email.sentBody, email.sentListingPagesSection).trim() !== email.bodyDraft.trim()
  );
}

/** D3 — who sent it and how, the expanded body's first line (the pill carries
 *  only the status). */
function IntroSentLine({ version }: { version: BuyerSendVersionDto }) {
  const { date } = useDateFormatter();
  const via = version.combinedIntroEmailId
    ? "a combined intro email"
    : version.selfAttested === true
      ? "own email"
      : version.selfAttested === false
        ? "app send"
        : null;
  const by = version.sentByDisplayName?.trim();
  return (
    <p className="text-small text-onyx m-0" data-testid="intro-email-sent-line">
      Sent {date(version.sentAtUtc)}
      {by ? ` by ${by}` : ""}
      {via ? ` via ${via}` : ""}
    </p>
  );
}

function NotDrafted({
  canWrite,
  onDraft,
  drafting,
}: {
  canWrite: boolean;
  onDraft: () => void;
  drafting: boolean;
}) {
  if (!canWrite) {
    return (
      <p className="text-small text-gray-mid m-0">
        No intro email has been drafted yet. Only this submission&apos;s
        consultant can draft one.
      </p>
    );
  }

  return (
    <EmptyState
      title="No intro email drafted yet"
      description={
        "Drafting is its own step, so you read the email before the buyer does. " +
        "Nothing is sent until you send it."
      }
      className="py-space-5"
    >
      {/* A real disabled Button rather than the `action` slot, which has no
          disabled state — a double-click there would buy two AI generations. */}
      <div className="mt-space-4">
        <Button onClick={onDraft} disabled={drafting}>
          {drafting ? "Drafting…" : "Draft intro email"}
        </Button>
      </div>
    </EmptyState>
  );
}

/**
 * The draft, editable, with the send controls beneath it.
 *
 * <p>Local state seeds from the server body and `dirty` is measured against the
 * LIVE prop, so a successful save clears it when the refetch lands rather than
 * leaving the send buttons stuck.</p>
 */
function IntroEmailComposer({
  submissionId,
  email,
  draft,
  onDraftChange,
  dirty,
  empty,
  onSend,
  onCopy,
  onRedraft,
  redrafting,
  includeListingLink,
  onIncludeListingLinkChange,
}: {
  submissionId: string;
  email: BuyerIntroEmailStateDto;
  /** The typed draft — held by the panel so a collapse keeps it (D2). */
  draft: string;
  onDraftChange: (next: string) => void;
  dirty: boolean;
  empty: boolean;
  onSend: () => void;
  onCopy: () => void;
  onRedraft: () => void;
  redrafting: boolean;
  includeListingLink: boolean;
  onIncludeListingLinkChange: (next: boolean) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const save = useMutation({
    mutationFn: () =>
      buyerSubmissionsApi.updateIntroEmail(submissionId, { body: draft }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success("Draft saved.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const alreadySent = Boolean(email.sentAtUtc);

  return (
    <div className="grid gap-space-3">
      <p className="text-small text-gray-mid m-0">
        Read it through and change anything that is not right — it goes to the
        buyer under your name. It reports only what the listing itself states;
        where your buyer&apos;s criteria have no counterpart in the listing, it
        deliberately says nothing about them.
      </p>

      <label className="grid gap-space-2">
        <span className="text-small font-medium">Subject</span>
        {/* Composed server-side and not editable — there is nothing to keep, so
            there is nothing to edit. Shown because the specialist should see
            the whole message, not most of it. */}
        <p className="text-small text-onyx m-0 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
          {email.subject}
        </p>
      </label>

      <label className="grid gap-space-2">
        <span className="text-small font-medium">Message</span>
        <Textarea
          rows={14}
          value={draft}
          data-testid="intro-email-body"
          aria-label="Intro email message"
          onChange={(e) => onDraftChange(e.target.value)}
        />
      </label>

      <ListingPageToggle
        line={email.listingPageLine ?? null}
        checked={includeListingLink}
        onChange={onIncludeListingLinkChange}
      />

      <div className="flex flex-wrap items-center gap-space-3">
        <Button
          variant="secondary"
          onClick={() => save.mutate()}
          disabled={!dirty || empty || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save draft"}
        </Button>
        <span className="text-caption text-gray-mid">
          {email.generatedAtUtc ? (
            <>
              Drafted <Timestamp value={email.generatedAtUtc} />
              {email.aiModel
                ? " by the assistant."
                : " without the assistant — composed straight from the listing's facts."}
            </>
          ) : null}
        </span>
      </div>

      {/* 🔴 The send uses the SAVED draft. Without this the specialist edits a
          sentence, sends, and the buyer receives the version they just fixed. */}
      {dirty ? (
        <p
          role="status"
          className="text-small text-status-warning m-0"
          data-testid="intro-email-unsaved"
        >
          {UNSAVED}
        </p>
      ) : null}

      {/* Server-resolved, and the only reason rendered. `canSend` and this
          sentence come from the same rule the endpoint refuses on. */}
      {!email.canSend && email.blockedReason ? (
        <p className="text-small text-status-warning m-0" data-testid="intro-email-blocked">
          {email.blockedReason}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-space-3">
        <Button onClick={onSend} disabled={dirty || empty || !email.canSend}>
          {alreadySent ? "Send again from Phoenix" : "Send from Phoenix"}
        </Button>
        {/*
          NOT gated on `canSend`. A buyer with no address on file blocks the
          Phoenix path only — the server's own refusal says "or take the copy
          and send it from your own email", and disabling this button would
          make that sentence false.
        */}
        <Button variant="secondary" onClick={onCopy} disabled={dirty || empty}>
          Copy for my own email
        </Button>
        <Button variant="text" onClick={onRedraft} disabled={redrafting}>
          {redrafting ? "Redrafting…" : "Redraft"}
        </Button>
      </div>

      <p className="text-caption text-gray-mid m-0">
        Either path records this as sent, so the buyer never looks uncontacted.
      </p>
    </div>
  );
}

/**
 * R3 — "Include the listing page", the single intro's send-time choice.
 *
 * <p>The line is the SERVER's (`listingPageLine`, copy C7), shown exactly as it
 * will go and never editable: it is added after the message, so a specialist
 * who wants to say something about the page says it in the message. A listing
 * with no absolute https page on file offers no checkbox at all — a toggle that
 * can add nothing is a control that cannot do what it appears to do.</p>
 */
function ListingPageToggle({
  line,
  checked,
  onChange,
}: {
  line: string | null;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  if (line == null) {
    return (
      <p className="text-small text-gray-mid m-0" data-testid="intro-email-no-listing-page">
        No https listing page on file.
      </p>
    );
  }

  return (
    <div className="grid gap-space-2">
      <label className="flex items-center gap-space-2 text-small">
        <input
          type="checkbox"
          checked={checked}
          data-testid="intro-email-include-listing-page"
          onChange={(e) => onChange(e.target.checked)}
        />
        Include the listing page
      </label>
      {checked ? (
        <div className="grid gap-space-1" data-testid="intro-email-listing-page-line">
          <div className="rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
            <p className="text-small text-onyx whitespace-pre-wrap break-all m-0">{line}</p>
          </div>
          <span className="text-caption text-gray-mid">
            Added below your message exactly as shown. Not editable.
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** What a colleague without the write sees: the draft, read-only. Seeing what a
 *  buyer was told is not a write. */
function ReadOnlyDraft({ email }: { email: BuyerIntroEmailStateDto }) {
  return (
    <div className="grid gap-space-2">
      <span className="text-small font-medium">Current draft</span>
      <BodyBlock body={email.bodyDraft} />
      <p className="text-caption text-gray-mid m-0">
        Only this submission&apos;s consultant can edit or send it.
      </p>
    </div>
  );
}

/**
 * What went out (D7, D9).
 *
 * <p>A single send is folded to three lines. A combined send's body is the
 * WHOLE email: the card shows this listing's own entry when `combinedEntry` can
 * find it by its number (J4), the summary line, and the full email behind View
 * full email in today's bounded box; with no reliable entry, the summary line
 * and the folded full email only — never a guess.</p>
 *
 * <p>Deliberately says "the latest version sent" rather than dating the
 * snapshot: the server stamps `IntroEmailSentAtUtc` forward-only (`??=`), so
 * the column is the FIRST send while the snapshot is the newest body. The date
 * the card shows is the latest send's (J2).</p>
 */
function SentSnapshot({ email, drifted }: { email: BuyerIntroEmailStateDto; drifted: boolean }) {
  const { date } = useDateFormatter();
  const combined = email.latestSendCombinedIntroEmailId != null;

  if (!email.sentBody) {
    return (
      <div className="grid gap-space-2" data-testid="intro-email-sent">
        <span className="text-small font-medium">{introCardCopy.whatWentOut}</span>
        <p className="text-small text-gray-mid m-0">
          This intro email was recorded as sent, but no copy of the message was
          kept.
        </p>
      </div>
    );
  }

  if (combined) {
    const count = (email.latestSendCombinedOtherCount ?? 0) + 1;
    const entry = combinedEntry(
      email.sentBody,
      email.sentListingPagesSection,
      email.latestSendCombinedPosition,
      count,
    );
    const name = email.buyerFirstName?.trim() || email.buyerName;
    return (
      <div className="grid gap-space-2" data-testid="intro-email-sent">
        <span className="text-small font-medium">{introCardCopy.whatWentOut}</span>
        {entry ? (
          <div className="rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
            <p className="text-small text-onyx whitespace-pre-wrap m-0" data-testid="intro-email-combined-entry">
              {entry}
            </p>
          </div>
        ) : null}
        {email.latestSendCombinedAtUtc ? (
          <p className="text-small text-status-info m-0" data-testid="intro-email-sent-combined">
            {introCardCopy.sentAsPartOf(name, count, date(email.latestSendCombinedAtUtc))}
          </p>
        ) : null}
        <FullEmailToggle body={email.sentBody} />
      </div>
    );
  }

  return (
    <SentFold body={email.sentBody} testId="intro-email-sent">
      {drifted ? (
        <p className="text-small text-status-info m-0" data-testid="intro-email-drift">
          The draft below has been edited since this went out.
        </p>
      ) : null}
    </SentFold>
  );
}

/** D9 — the whole combined email, behind View full email, in today's bounded
 *  scroll box. */
function FullEmailToggle({ body }: { body: string }) {
  const [shown, setShown] = useState(false);
  const id = useId();
  return (
    <div className="grid gap-space-2">
      <div>
        <Button
          variant="text"
          size="sm"
          aria-expanded={shown}
          aria-controls={id}
          onClick={() => setShown((v) => !v)}
          data-testid="intro-email-view-full"
        >
          {shown ? introCardCopy.hideFullEmail : introCardCopy.viewFullEmail}
        </Button>
      </div>
      <div id={id} hidden={!shown} data-testid="intro-email-full-email">
        <BodyBlock body={body} />
      </div>
    </div>
  );
}

function BodyBlock({ body }: { body: string | null }) {
  if (!body) {
    return <p className="text-small text-gray-mid m-0">Nothing drafted.</p>;
  }
  return (
    <div className="max-h-80 overflow-y-auto rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
      <p className="text-small text-onyx whitespace-pre-wrap m-0">{body}</p>
    </div>
  );
}
